import { toast } from 'sonner';

import { ACCEPTED_MEDIA_TYPES } from '../../constants/mediaTypes';
import { typedError } from '../../lib/typedError';
import type { MediaUploadInputProps } from '../../types/fileUpload';
import { getMediaType, validateMediaFile } from '../../utils/mediaUtils';
import { BaseUploadInput } from './BaseUploadInput';

const MEDIA_ACCEPT =
  'image/jpeg,image/jpg,image/png,image/webp,video/mp4,.jpg,.jpeg,.png,.webp,.mp4';

export function MediaUploadInput({
  name,
  label,
  description,
  dimensions,
  value = null,
  onChange,
  onError,
  className,
  placeholderClassName,
  buttonType = 'add',
  buttonClassName,
}: MediaUploadInputProps) {
  const validateFile = (file: File) => {
    const result = validateMediaFile(file);
    if (!result.valid && result.error) {
      if (result.error.type === 'FileSizeExceeded') {
        toast.error(result.error.message);
      }
      return {
        valid: false,
        error: typedError(result.error.type, result.error.message),
      };
    }
    return { valid: true };
  };

  const renderPreview = (url: string, file: File) => {
    const mediaType = getMediaType(file);
    if (mediaType === 'video') {
      return (
        <video
          src={url}
          aria-label={`${label || name} preview`}
          controls
          autoPlay
          muted
          loop
          playsInline
          className="block w-full h-full object-cover"
        />
      );
    }
    return (
      <img
        src={url}
        alt={`${label || name} preview`}
        className="block w-full h-full object-cover"
      />
    );
  };

  return (
    <BaseUploadInput
      name={name}
      label={label}
      description={description}
      dimensions={dimensions}
      value={value}
      onChange={onChange}
      onError={onError}
      className={className}
      placeholderClassName={placeholderClassName}
      buttonType={buttonType}
      buttonClassName={buttonClassName}
      accept={MEDIA_ACCEPT}
      acceptedTypes={ACCEPTED_MEDIA_TYPES}
      validateFile={validateFile}
      renderPreview={renderPreview}
    />
  );
}
